import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { useContractApi } from 'api/contract';

/**
 * The shared Apryse WebViewer is heavy (`@pdftron/webviewer`); lazy-load it so it stays
 * out of the View Deal chunk until a PDF/Office deliverable is actually opened.
 */
const DocumentViewer = lazy(() => import('shared/DocumentViewer'));

export interface ApryseDeliverableViewerProps {
  /** Presigned S3 URL for the document bytes. */
  downloadUrl?: string;
  /** Original file name — drives Apryse's format detection via the File's `.name`. */
  fileName: string;
  /** MIME type from the list DTO, when known. */
  mimeType?: string;
}

type FileState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; file: File };

/**
 * Fetches the document bytes (reusing `getBlobFromFileURL`) and wraps them in a `File`
 * named for Apryse. The fetcher comes from `useContractApi`, which builds a fresh axios
 * instance every render; a latest-value ref keeps it out of the effect deps so the
 * effect re-runs only on real input changes (URL/name/mime) or an explicit retry.
 * @param {ApryseDeliverableViewerProps & { attempt: number }} input - Document inputs plus a retry counter.
 * @returns {FileState} The current fetch/wrap state.
 */
function useDeliverableFile({
  downloadUrl,
  fileName,
  mimeType,
  attempt
}: ApryseDeliverableViewerProps & { attempt: number }): FileState {
  const { getBlobFromFileURL } = useContractApi();
  const fetchRef = useRef(getBlobFromFileURL);
  fetchRef.current = getBlobFromFileURL;
  const [state, setState] = useState<FileState>({ status: 'loading' });

  useEffect(() => {
    if (!downloadUrl) {
      setState({ status: 'error' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetchRef
      .current(downloadUrl)
      .then((blob) => {
        if (cancelled) return;
        if (!blob) {
          setState({ status: 'error' });
          return;
        }
        setState({ status: 'ready', file: new File([blob], fileName, mimeType ? { type: mimeType } : undefined) });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [downloadUrl, fileName, mimeType, attempt]);

  return state;
}

/**
 * Renders a PDF/DOCX/XLSX deliverable in the existing shared Apryse `DocumentViewer`
 * (ADR-0003 Decision 6). It fetches the presigned bytes, hands the viewer a named
 * `File`, and surfaces loading/error states (with retry) around it.
 * @param {ApryseDeliverableViewerProps} props - The presigned URL, file name, and MIME type.
 * @returns {React.ReactElement} The Apryse viewer, or a loading/error state.
 */
export function ApryseDeliverableViewer(props: ApryseDeliverableViewerProps): React.ReactElement {
  const [attempt, setAttempt] = useState(0);
  const state = useDeliverableFile({ ...props, attempt });

  if (state.status === 'loading') {
    return (
      <div className="h-full flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <Alert
          type="error"
          showIcon
          message="Unable to load deliverable"
          description="The document could not be fetched."
          action={
            <Button size="small" onClick={() => setAttempt((n) => n + 1)}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="h-full">
      <Suspense
        fallback={
          <div className="h-full flex items-center justify-center">
            <Spin size="large" />
          </div>
        }
      >
        <DocumentViewer documentTobeLoaded={state.file} tracking={{ viewerContext: 'deal_deliverables' }} />
      </Suspense>
    </div>
  );
}
